import { flash } from "@xola/ui-kit";
import { produce } from "immer";
import { useEffect, useState } from "react";

/**
 * Custom hook, similar to `useState`, but with some differences. Best used for optimistic UI updates.
 * Allows us to modify the specified resource in the background while having snappy UI updates.
 * Uses `immer` for easier data modifications.
 */
export const useOptimisticUpdate = <T>(data: T | null, isLoading: boolean, onUpdate: (data: T) => void) => {
    const [currentData, setCurrentData] = useState<T | null>(data);

    // Sync `currentData` with outside.
    useEffect(() => {
        setCurrentData(data);
    }, [setCurrentData, data]);

    const updateData = (callback: (data: T) => void) => {
        if (isLoading) {
            return flash.show({ text: "Still working, please wait..." });
        }

        const updatedData = produce(currentData, callback)!;
        setCurrentData(updatedData);
        onUpdate(updatedData);
    };

    return [currentData, updateData] as unknown as [T, (callback: (data: T) => void) => void];
};
